import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, FlaskConical, CheckCircle, Clock,
  PackageCheck, AlertTriangle, RotateCcw, X
} from 'lucide-react';

function formatDateFr(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR') + ' ' +
      d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function LigneForm({ ligne, index, inventaire, onChange, onRemove }) {
  const [search, setSearch] = useState(ligne.ingredient || '');
  const [showList, setShowList] = useState(false);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return inventaire.slice(0, 8);
    return inventaire.filter(i =>
      (i.ingredient || '').toLowerCase().includes(q)).slice(0, 8);
  }, [search, inventaire]);

  const pickItem = (item) => {
    setSearch(item.ingredient);
    setShowList(false);
    onChange(index, {
      ...ligne,
      item_id: item.id, ingredient: item.ingredient,
      zone: item.zone || '', categorie: item.categorie || '',
      unite: item.unite || '',
    });
  };

  const handleText = (val) => {
    setSearch(val);
    setShowList(true);
    onChange(index, { ...ligne, item_id: '', ingredient: val, zone: '', categorie: '' });
  };

  return (
    <div className="border rounded-lg p-3 space-y-2 relative bg-muted/30">
      <div className="flex items-start gap-2">
        <div className="flex-1 relative">
          <Label className="text-xs">Article</Label>
          <Input
            value={search}
            placeholder="Rechercher dans l'inventaire ou saisir un nom..."
            onChange={(e) => handleText(e.target.value)}
            onFocus={() => setShowList(true)}
            onBlur={() => setTimeout(() => setShowList(false), 200)}
          />
          {showList && filtered.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-56 overflow-y-auto">
              {filtered.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex justify-between items-center"
                  onMouseDown={(e) => { e.preventDefault(); pickItem(item); }}
                >
                  <span>{item.ingredient}</span>
                  <span className="text-xs text-muted-foreground">{item.zone}{item.unite ? ` · ${item.unite}` : ''}</span>
                </button>
              ))}
            </div>
          )}
          {ligne.item_id ? (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <PackageCheck className="w-3 h-3" /> Lié à l'inventaire ({ligne.zone})
            </p>
          ) : search ? (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Article libre (pas de mise à jour auto du stock)
            </p>
          ) : null}
        </div>
        <div className="w-24">
          <Label className="text-xs">Quantité</Label>
          <Input
            type="number" step="0.01" min="0"
            value={ligne.quantite}
            onChange={(e) => onChange(index, { ...ligne, quantite: e.target.value })}
          />
        </div>
        <div className="w-20">
          <Label className="text-xs">Unité</Label>
          <Input
            value={ligne.unite}
            placeholder="kg, pcs..."
            onChange={(e) => onChange(index, { ...ligne, unite: e.target.value })}
          />
        </div>
        <div className="w-36">
          <Label className="text-xs">DLC (optionnel)</Label>
          <Input
            type="date"
            value={ligne.dlc}
            onChange={(e) => onChange(index, { ...ligne, dlc: e.target.value })}
          />
        </div>
        <Button variant="ghost" size="icon" className="mt-5 text-destructive" onClick={() => onRemove(index)}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function LivraisonCard({ liv, isAdmin, onPointer, onDepointer, onDelete, busy }) {
  const restantes = liv.lignes.filter(l => !l.pointe).length;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-primary" />
            Livraison du {liv.date_livraison ? new Date(liv.date_livraison + 'T00:00:00').toLocaleDateString('fr-FR') : '?'}
            {liv.statut === 'validee' ? (
              <Badge className="bg-green-600 hover:bg-green-600">Tout pointé</Badge>
            ) : liv.statut === 'partielle' ? (
              <Badge className="bg-amber-500 hover:bg-amber-500">{restantes} restant{restantes > 1 ? 's' : ''}</Badge>
            ) : (
              <Badge variant="destructive">À pointer</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Déclarée par {liv.cree_par_nom || liv.cree_par}</span>
            {isAdmin && liv.statut === 'en_attente' && (
              <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => onDelete(liv)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {liv.notes && <p className="text-sm text-muted-foreground">{liv.notes}</p>}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Article</TableHead>
                <TableHead>Quantité</TableHead>
                <TableHead>DLC</TableHead>
                <TableHead>Entré en inventaire</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {liv.lignes.map(l => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium">
                    {l.ingredient}
                    {l.zone && <span className="text-xs text-muted-foreground ml-2">{l.zone}</span>}
                  </TableCell>
                  <TableCell>{l.quantite} {l.unite}</TableCell>
                  <TableCell>{l.dlc ? new Date(l.dlc + 'T00:00:00').toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell>
                    {l.pointe ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-green-600 hover:bg-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> {l.pointe_par}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatDateFr(l.pointe_datetime)}</span>
                        {!l.entree_stock && (
                          <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
                            stock non lié
                          </Badge>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Annuler le pointage"
                            disabled={busy} onClick={() => onDepointer(liv, l)}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button size="sm" disabled={busy} onClick={() => onPointer(liv, l)}
                        data-testid={`pointer-${l.id}`}>
                        <PackageCheck className="w-4 h-4 mr-1" /> Entré en inventaire
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export function LivraisonsLaboPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [livraisons, setLivraisons] = useState([]);
  const [inventaire, setInventaire] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const emptyLigne = { item_id: '', ingredient: '', zone: '', categorie: '', unite: '', quantite: '', dlc: '' };
  const [form, setForm] = useState({ date_livraison: todayStr, notes: '', lignes: [{ ...emptyLigne }] });

  const fetchData = useCallback(async () => {
    try {
      const [lRes, iRes] = await Promise.all([
        api.get('/livraisons-labo'),
        api.get('/inventaire'),
      ]);
      setLivraisons(lRes.data);
      setInventaire(iRes.data.sort((a, b) => (a.ingredient || '').localeCompare(b.ingredient || '')));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const aTraiter = livraisons.filter(l => l.statut !== 'validee');
  const historique = livraisons.filter(l => l.statut === 'validee');
  const articlesRestants = aTraiter.reduce((n, l) => n + l.lignes.filter(x => !x.pointe).length, 0);

  const changeLigne = (idx, ligne) => {
    setForm(f => ({ ...f, lignes: f.lignes.map((l, i) => i === idx ? ligne : l) }));
  };
  const removeLigne = (idx) => {
    setForm(f => ({ ...f, lignes: f.lignes.filter((_, i) => i !== idx) }));
  };
  const addLigne = () => setForm(f => ({ ...f, lignes: [...f.lignes, { ...emptyLigne }] }));

  const handleCreate = async () => {
    const lignes = form.lignes.filter(l => (l.ingredient || '').trim());
    if (lignes.length === 0) { toast.error('Ajoute au moins un article'); return; }
    for (const l of lignes) {
      if (!l.quantite || parseFloat(l.quantite) <= 0) {
        toast.error(`Quantité manquante pour ${l.ingredient}`); return;
      }
    }
    setBusy(true);
    try {
      await api.post('/livraisons-labo', { ...form, lignes });
      toast.success('Livraison déclarée, en attente de pointage par l\'équipe');
      setDialogOpen(false);
      setForm({ date_livraison: todayStr, notes: '', lignes: [{ ...emptyLigne }] });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setBusy(false); }
  };

  const handlePointer = async (liv, ligne) => {
    setBusy(true);
    try {
      const res = await api.post(`/livraisons-labo/${liv.id}/lignes/${ligne.id}/pointer`);
      setLivraisons(ls => ls.map(l => l.id === liv.id ? res.data : l));
      const pointee = res.data.lignes.find(x => x.id === ligne.id);
      if (pointee?.entree_stock) {
        toast.success(`${ligne.ingredient} : +${ligne.quantite} ${ligne.unite || ''} ajouté au stock inventaire`);
      } else {
        toast.success(`${ligne.ingredient} pointé. Article non lié : pense à le saisir dans l'inventaire.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du pointage');
    } finally { setBusy(false); }
  };

  const handleDepointer = async (liv, ligne) => {
    if (!window.confirm(`Annuler le pointage de ${ligne.ingredient} ? Le stock sera corrigé.`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/livraisons-labo/${liv.id}/lignes/${ligne.id}/depointer`);
      setLivraisons(ls => ls.map(l => l.id === liv.id ? res.data : l));
      toast.success('Pointage annulé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleDelete = async (liv) => {
    if (!window.confirm('Supprimer cette livraison ?')) return;
    try {
      await api.delete(`/livraisons-labo/${liv.id}`);
      setLivraisons(ls => ls.filter(l => l.id !== liv.id));
      toast.success('Livraison supprimée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-primary" /> Livraisons Labo
          </h1>
          <p className="text-muted-foreground text-sm">
            Déclaration des livraisons du labo et pointage en inventaire par l'équipe
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} data-testid="nouvelle-livraison">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle livraison
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-2xl font-bold">{aTraiter.length}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Livraisons à traiter</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className={`text-2xl font-bold ${articlesRestants > 0 ? 'text-amber-600' : 'text-green-600'}`}>{articlesRestants}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><PackageCheck className="w-3 h-3" /> Articles à pointer</p>
        </CardContent></Card>
        <Card className="col-span-2 md:col-span-1"><CardContent className="pt-4 pb-3">
          <p className="text-2xl font-bold">{historique.length}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Livraisons complètes</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="a-traiter">
        <TabsList>
          <TabsTrigger value="a-traiter">
            À pointer {aTraiter.length > 0 && <Badge variant="destructive" className="ml-2">{aTraiter.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="historique">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="a-traiter" className="space-y-4 mt-4">
          {aTraiter.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-600" />
              Aucune livraison en attente, tout est pointé.
            </CardContent></Card>
          ) : aTraiter.map(liv => (
            <LivraisonCard key={liv.id} liv={liv} isAdmin={isAdmin} busy={busy}
              onPointer={handlePointer} onDepointer={handleDepointer} onDelete={handleDelete} />
          ))}
        </TabsContent>
        <TabsContent value="historique" className="space-y-4 mt-4">
          {historique.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              Aucune livraison complète pour le moment.
            </CardContent></Card>
          ) : historique.map(liv => (
            <LivraisonCard key={liv.id} liv={liv} isAdmin={isAdmin} busy={busy}
              onPointer={handlePointer} onDepointer={handleDepointer} onDelete={handleDelete} />
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle livraison labo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div>
                <Label>Date de livraison</Label>
                <Input type="date" value={form.date_livraison}
                  onChange={(e) => setForm(f => ({ ...f, date_livraison: e.target.value }))} />
              </div>
              <div className="flex-1">
                <Label>Notes (optionnel)</Label>
                <Textarea rows={1} value={form.notes} placeholder="Ex : livraison du matin, glacière bleue..."
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Articles livrés</Label>
              {form.lignes.map((l, i) => (
                <LigneForm key={i} ligne={l} index={i} inventaire={inventaire}
                  onChange={changeLigne} onRemove={removeLigne} />
              ))}
              <Button variant="outline" size="sm" onClick={addLigne}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter un article
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Déclarer la livraison
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
